// Working Atlas Viewer - Following Official Examples
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import WelcomeBanner from './components/ui/WelcomeBanner';
import { ThemeProvider } from 'styled-components';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import { injectComponents } from '@kepler.gl/components';
import { integralAnalyticsTheme } from './theme';
import { replacePanelHeader } from './factories/panel-header';
import { replaceLoadDataModal } from './factories/add-data-modal';
import { replaceMapControl } from './factories/map-control';
import { replaceSidePanel } from './factories/side-panel';
import store from './store';
import { toggleModal, wrapTo } from '@kepler.gl/actions';
import ReleaseNotesModal from './components/release-notes/ReleaseNotesModal';
import IssueSubmissionModal from './components/issue-form/IssueSubmissionModal';
import { env } from './env.config';
import {
  SqlPanel,
  useSqlPanel,
  setupSqlPanelToggle,
  useDuckDBPreload,
} from './integrations/sql-panel';
import { FeatureHoverTooltip } from './integrations/feature-annotations';

// Import editor helpers in development for console access
import './utils/editor-helpers';

// Inject custom components into KeplerGl
// NOTE: Order matters. Kepler's provideRecipesToInjector maps ALL deps of each replacement
// to identity before applying the replacement itself. Any replacement whose deps include
// a factory that another replacement targets MUST come BEFORE that other replacement,
// otherwise the later identity-mapping step will override the earlier custom mapping.
// CustomSidePanelFactory.deps === SidePanelFactory.deps, which includes PanelHeaderFactory.
// So replaceSidePanel() must run FIRST so that replacePanelHeader() can override the
// identity mapping PanelHeaderFactory→PanelHeaderFactory back to our custom factory.
const CustomKeplerGl = injectComponents([
  replaceSidePanel(),
  replacePanelHeader(),
  replaceLoadDataModal(),
  replaceMapControl(),
] as never[]);

// Environment variables for Mapbox configuration
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';
const MAPBOX_STYLE_URL = import.meta.env.VITE_MAPBOX_STYLE_URL || '';

// Default map view settings and app branding from constants
import {
  APP_TITLE,
  APP_DESCRIPTION,
  ATLAS_DARK_STYLE_ID,
  ATLAS_DARK_STYLE_LABEL,
  ATLAS_DARK_STYLE_ICON,
  DEFAULT_MAP_LATITUDE,
  DEFAULT_MAP_LONGITUDE,
  DEFAULT_MAP_ZOOM,
} from './constants/default-settings';

import { isValidMapboxConfig, extractStyleIdFromUrl } from './utils/mapbox-config';
import { resolveAssetsPath } from './utils/assets';

// Helper function to generate Mapbox static image URL
const generateMapboxStaticUrl = (userStyleId: string, token: string): string => {
  return `https://api.mapbox.com/styles/v1/${userStyleId}/static/${DEFAULT_MAP_LONGITUDE},${DEFAULT_MAP_LATITUDE},${DEFAULT_MAP_ZOOM}/128x128?access_token=${token}`;
};

// Create custom map styles array for KeplerGl - only if Mapbox config is valid
const customMapStyles = isValidMapboxConfig()
  ? [
      {
        id: ATLAS_DARK_STYLE_ID,
        label: ATLAS_DARK_STYLE_LABEL,
        url: MAPBOX_STYLE_URL,
        icon: extractStyleIdFromUrl(MAPBOX_STYLE_URL)
          ? generateMapboxStaticUrl(extractStyleIdFromUrl(MAPBOX_STYLE_URL)!, MAPBOX_TOKEN)
          : resolveAssetsPath(ATLAS_DARK_STYLE_ICON.replace('assets/', '')),
        accessToken: MAPBOX_TOKEN,
      },
    ]
  : [];

console.log('Atlas Viewer Custom Map Styles:', {
  hasValidMapboxConfig: isValidMapboxConfig(),
  customStylesCount: customMapStyles.length,
  mapboxToken: MAPBOX_TOKEN ? `${MAPBOX_TOKEN.substring(0, 6)}...` : 'none',
  styleUrl: MAPBOX_STYLE_URL,
});

// Helper functions with proper typing
const keplerGlGetState = (state: Record<string, unknown>) => state.keplerGl;

// Container styles
const CONTAINER_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  minHeight: '70vh',
  background: integralAnalyticsTheme?.sidePanelBg || '#181A1B',
};

// Shallow prop filter helper for root container to prevent invalid attributes reaching DOM
const FILTER_PROPS = new Set([
  'backgroundColor',
  'isConfigActive',
  'isEditingLabel',
  'isValid',
  'active',
  'labelRCGColorValues',
  // kepler modal/button transient props that should not hit raw DOM
  'link',
  'large',
  'cta',
]);

const FilterDiv: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => {
  const clean: Record<string, unknown> = {};
  Object.entries(rest).forEach(([k, v]) => {
    if (FILTER_PROPS.has(k)) return; // drop
    clean[k] = v;
  });
  return <div {...clean}>{children}</div>;
};

const App = () => {
  const [showReleaseNotesPanel, setShowReleaseNotesPanel] = useState(false);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const { toggle: toggleSqlPanelState } = useSqlPanel();

  // Pre-initialize DuckDB when datasets are loaded (reduces SQL panel open latency)
  useDuckDBPreload();

  // Set document title and description dynamically for SPA navigation/SEO
  useEffect(() => {
    document.title = APP_TITLE;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', APP_DESCRIPTION);
  }, []);

  // Setup global panel toggles for panel header
  useEffect(() => {
    (window as unknown as Record<string, unknown>).toggleReleaseNotesPanel = () =>
      setShowReleaseNotesPanel((v: boolean) => !v);
    if (env.VITE_ISSUE_SUBMISSION_ENABLED) {
      (window as unknown as Record<string, unknown>).toggleIssueFormPanel = () =>
        setShowIssueForm((v: boolean) => !v);
    }
    setupSqlPanelToggle(toggleSqlPanelState);
    (window as any).openAddDataModal = () => {
      try {
        store.dispatch(wrapTo('map', toggleModal('addData')) as any);
      } catch {
        const btn = document.querySelector(
          '[data-testid="action-storage"],button[id*="storage"],button[aria-label*="Add data" i]'
        );
        (btn as HTMLButtonElement | null)?.click();
      }
    };
    return () => {
      (window as unknown as Record<string, unknown>).toggleReleaseNotesPanel = undefined;
      (window as unknown as Record<string, unknown>).toggleIssueFormPanel = undefined;
      (window as any).toggleSqlPanel = undefined;
      (window as any).openAddDataModal = undefined;
    };
  }, [toggleSqlPanelState]);

  return (
    <ThemeProvider theme={integralAnalyticsTheme}>
      <FilterDiv style={CONTAINER_STYLE}>
        <WelcomeBanner />
        {/*
          AutoSizer automatically handles window resize events via its built-in ResizeObserver.
          We removed the forced remount pattern (via changing key prop) that was causing
          the app to reload and lose all data on window resize.

          Tradeoff: This fixes #19 (data loss on resize) but removes cross-display resize reliability.
          Manual testing shows this works correctly for typical window resizing. If cross-display
          issues resurface, consider adding E2E test: e2e/window-resize-data-persistence.spec.ts
        */}
        <AutoSizer>
          {({ height, width }) => (
            <CustomKeplerGl
              mapboxApiAccessToken={MAPBOX_TOKEN}
              id='map'
              getState={keplerGlGetState}
              width={width}
              height={height}
              mapStyles={customMapStyles}
              theme={integralAnalyticsTheme}
            />
          )}
        </AutoSizer>
        <ReleaseNotesModal
          isOpen={showReleaseNotesPanel}
          onClose={() => setShowReleaseNotesPanel(false)}
          theme={integralAnalyticsTheme}
        />
        {env.VITE_ISSUE_SUBMISSION_ENABLED && (
          <IssueSubmissionModal isOpen={showIssueForm} onClose={() => setShowIssueForm(false)} />
        )}
        <SqlPanel />
        <FeatureHoverTooltip />
        {/* SQL Panel toggle button is now rendered as part of MapControl action components */}
      </FilterDiv>
    </ThemeProvider>
  );
};

export default App;
